import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { MailCheck, PartyPopper } from '@/components/icons';
import { ROLE_LABELS } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { Badge, Button, ErrorNotice, Field, Input, PageLoader } from '@/components/ui';

export function AcceptInvitePage() {
  const { token = '' } = useParams();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const preview = useQuery({ queryKey: ['invitation', token], queryFn: () => api.invitationPreview(token), retry: false });

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (tooShort || mismatch) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.acceptInvitation(token, name.trim(), password);
      await signIn(res.token);
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      {preview.isPending ? (
        <PageLoader label="Checking your invitation…" />
      ) : preview.isError ? (
        <div className="space-y-4">
          <h1 className="text-[32px]/10 font-semibold tracking-[-0.04em] text-fg">Invitation not valid</h1>
          <ErrorNotice error={preview.error} />
          <p className="text-sm text-fg-secondary">The link may have expired or been revoked. Ask the sender for a new invitation.</p>
        </div>
      ) : (
        <>
          <div className="mb-4 grid size-12 place-items-center rounded-lg bg-primary-subtle text-primary">
            <PartyPopper className="size-6" />
          </div>
          <h1 className="text-[32px]/10 font-semibold tracking-[-0.04em] text-fg">You're invited to 1QA</h1>
          <p className="mt-2 text-sm text-fg-secondary">
            {preview.data.orgName ? (
              <>
                Join <strong>{preview.data.orgName}</strong> as{' '}
                <Badge tone="brand">{ROLE_LABELS[preview.data.orgRole ?? 'MEMBER']}</Badge>
              </>
            ) : (
              <>
                You'll join as <Badge tone="brand">{ROLE_LABELS[preview.data.platformRole ?? 'USER']}</Badge>
              </>
            )}
          </p>
          {preview.data.projects && preview.data.projects.length > 0 && (
            <p className="mt-3 text-sm text-fg-secondary">
              You'll work on <strong>{preview.data.projects.join(', ')}</strong>: view its tests and reports and run the tests.
            </p>
          )}
          <p className="mt-3 flex items-center gap-2 text-sm text-fg-muted">
            <MailCheck className="size-4" /> {preview.data.email}
          </p>
          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <Field label="Your name">
              {(id) => <Input id={id} required autoFocus autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />}
            </Field>
            <Field label="Create a password" hint="At least 8 characters." error={tooShort ? 'Use at least 8 characters.' : undefined}>
              {(id) => (
                <Input id={id} type="password" required autoComplete="new-password" invalid={tooShort} value={password} onChange={(e) => setPassword(e.target.value)} />
              )}
            </Field>
            <Field label="Confirm password" error={mismatch ? 'Passwords do not match.' : undefined}>
              {(id) => (
                <Input id={id} type="password" required autoComplete="new-password" invalid={mismatch} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              )}
            </Field>
            <p className="text-xs text-fg-muted">Already have a 1QA account with this email? Enter its password to link the invitation.</p>
            {error && <ErrorNotice error={new Error(error)} />}
            <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!name.trim() || password.length < 8 || confirm !== password}>
              Accept invitation
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
